import { useState } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "./Logo";
import {
  Home, BookOpen, FileText, ClipboardCheck, RefreshCw,
  FolderOpen, User, CreditCard, LogOut, Menu, X, Shield, LifeBuoy
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { RenewalBanner, useCurrentCharge } from "@/components/RenewalBanner";

const NAV = [
  { to: "/dashboard", label: "Início", icon: Home, testid: "nav-home" },
  { to: "/plano-aula", label: "Plano de Aula", icon: BookOpen, testid: "nav-plano" },
  { to: "/atividade", label: "Atividades", icon: FileText, testid: "nav-atividade" },
  { to: "/avaliacao", label: "Avaliações", icon: ClipboardCheck, testid: "nav-avaliacao" },
  { to: "/adaptar", label: "Adaptar Conteúdo", icon: RefreshCw, testid: "nav-adaptar" },
  { to: "/materiais", label: "Meus Materiais", icon: FolderOpen, testid: "nav-materiais" },
  { to: "/conta", label: "Minha Conta", icon: User, testid: "nav-conta" },
  { to: "/assinatura", label: "Assinatura", icon: CreditCard, testid: "nav-assinatura" },
  { to: "/suporte", label: "Ajuda e Suporte", icon: LifeBuoy, testid: "nav-suporte" },
];

function SidebarContent({ onNavigate }) {
  const { user, usage, logout } = useAuth();
  const navigate = useNavigate();
  const pct = usage ? Math.min(100, Math.round((usage.used / Math.max(1, usage.limit)) * 100)) : 0;
  const charge = useCurrentCharge();
  const chargeDot = charge?.show_banner ? (charge.is_overdue ? "bg-red-500" : "bg-amber-500") : null;

  return (
    <aside className="h-full w-72 bg-white border-r border-slate-200 flex flex-col">
      <div className="p-5 border-b border-slate-100">
        <Link to="/dashboard" onClick={onNavigate}><Logo /></Link>
      </div>
      <nav className="p-4 flex-1 overflow-y-auto space-y-1">
        {NAV.map(item => (
          <NavLink key={item.to} to={item.to} end onClick={onNavigate}
            data-testid={item.testid}
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#1F3A8A]/10 text-[#1F3A8A] border-l-4 border-[#8B5CF6] pl-2"
                  : "text-slate-600 hover:bg-slate-50"
              }`}>
            <item.icon size={18} strokeWidth={2} />
            {item.label}
            {item.to === "/assinatura" && chargeDot && (
              <span
                data-testid="sidebar-charge-dot"
                className={`absolute right-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ${chargeDot} ring-2 ring-white animate-pulse`}
                title={charge.is_overdue ? "Pagamento vencido" : "Pagamento próximo do vencimento"}
              />
            )}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/admin" onClick={onNavigate}
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors mt-4 ${
                isActive ? "bg-[#FBBF24]/20 text-[#0F172A]" : "text-slate-700 hover:bg-[#FBBF24]/10"
              }`}>
            <Shield size={18} /> Painel Admin
          </NavLink>
        )}
      </nav>

      {usage && usage.plan === "free" && (
        <div className="mx-4 mb-3 p-4 rounded-2xl border border-[#FBBF24]/40 bg-[#FBBF24]/10">
          <div className="flex justify-between text-xs font-semibold text-slate-700 mb-2">
            <span>Gerações grátis</span>
            <span data-testid="sidebar-usage">{usage.used}/{usage.limit}</span>
          </div>
          <Progress value={pct} className="h-2" />
          <Button size="sm" className="w-full mt-3 rounded-full bg-[#8B5CF6] hover:bg-[#7c4ef4]"
            onClick={() => { navigate("/assinatura"); onNavigate?.(); }}
            data-testid="sidebar-upgrade-btn">
            Assinar Premium
          </Button>
        </div>
      )}

      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name}
              referrerPolicy="no-referrer"
              className="w-10 h-10 rounded-full object-cover border border-slate-200"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="user-avatar"/>
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1F3A8A] to-[#8B5CF6] flex items-center justify-center text-white font-bold" data-testid="user-avatar-initial">
              {user?.name?.[0]?.toUpperCase() || "P"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-800 truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/"); }}
          data-testid="btn-logout"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors">
          <LogOut size={16} /> Sair
        </button>
      </div>
    </aside>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const { usage } = useAuth();

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-shrink-0"><SidebarContent /></div>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full"><SidebarContent onNavigate={() => setOpen(false)} /></div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="btn-open-menu" className="p-2 rounded-lg hover:bg-slate-100">
            <Menu size={22} />
          </button>
          <Logo size="sm" />
          {usage && (
            <div className="text-xs font-semibold bg-[#FBBF24]/20 text-[#0F172A] px-3 py-1 rounded-full" data-testid="mobile-usage">
              {usage.used}/{usage.limit}
            </div>
          )}
        </header>

        <main className="flex-1 min-w-0">
          <RenewalBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
